import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { QUEUES } from '../constants/webSocketConstants';
import { messageService } from '../services/messageService';

/**
 * Notification Context - Manages real-time notifications AND message unread counts globally
 * Subscribes to WebSocket ONCE and shares state across all components
 * 
 * Provides:
 * - notifications: Array of notification objects
 * - unreadCount: Number of unread notifications (tips, likes, comments, etc.)
 * - totalUnreadMessages: Total unread message count across all conversations
 * - messageUnreadCounts: Map of conversationId -> unreadCount
 */
const NotificationContext = createContext(null);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { subscribe, isConnected, user } = useWebSocket();
  
  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Message unread counts state (conversationId -> unreadCount)
  const [messageUnreadCounts, setMessageUnreadCounts] = useState(new Map());
  const [initialMessageCountFetched, setInitialMessageCountFetched] = useState(false);
  const [lastCounterUpdate, setLastCounterUpdate] = useState(null); // For useChatListUpdates to react to
  
  // Muted conversation IDs - set by Messages.js to exclude from total
  const [mutedConversationIds, setMutedConversationIds] = useState(new Set());
  
  // Calculate total unread messages from all conversations
  // If we have the __total__ key (from initial API fetch), use it (already excludes muted on backend)
  // Otherwise, sum up per-conversation counts excluding muted ones
  const totalUnreadMessages = useMemo(() => {
    // Check if we have a __total__ entry (from initial fetch - backend already excludes muted)
    if (messageUnreadCounts.has('__total__')) {
      return messageUnreadCounts.get('__total__');
    }
    // Sum up per-conversation counts, excluding muted conversations
    let total = 0;
    messageUnreadCounts.forEach((count, conversationId) => {
      if (!mutedConversationIds.has(conversationId)) {
        total += count;
      }
    });
    return total;
  }, [messageUnreadCounts, mutedConversationIds]);

  // Handle notification messages
  const handleNotification = useCallback((data) => {
    if (!data) return;

    const notification = {
      id: data.id || `notification-${Date.now()}-${Math.random()}`,
      type: data.type,
      title: data.title || 'Notification',
      message: data.body || data.message || '',
      
      // Database notification fields
      relatedUserId: data.relatedUserId,
      relatedUserUsername: data.relatedUserUsername,
      relatedEntityType: data.relatedEntityType,
      relatedEntityId: data.relatedEntityId,
      metadata: data.metadata || data.data || {},
      actionUrl: data.actionUrl,
      read: data.read || false,
      readAt: data.readAt,
      createdAt: data.createdAt || data.timestamp,
      expiresAt: data.expiresAt,
      
      // Legacy fields for compatibility
      data: data.data || data.metadata || {},
      timestamp: data.createdAt || data.timestamp || new Date().toISOString(),
      isRead: data.read || false,
      priority: data.priority || 'normal',
      category: data.category || 'notification'
    };

    // Add to notifications list (avoid duplicates)
    setNotifications(prev => {
      const exists = prev.some(n => n.id === notification.id);
      if (exists) {
        // Update existing notification
        return prev.map(n => 
          n.id === notification.id ? { ...n, ...notification } : n
        );
      }
      const newNotifications = [notification, ...prev];
      return newNotifications.slice(0, 100); // Keep last 100
    });

    // Update unread count
    if (!notification.isRead && !notification.read) {
      setUnreadCount(prev => prev + 1);
    }

    setLastUpdate(new Date());
  }, []);

  // Handle message unread counter updates from QUEUES.COUNTERS
  const handleCounterUpdate = useCallback((message) => {
    if (!message) return;
    
    try {
      let data = message;
      
      // Parse if string
      if (typeof message === 'string') {
        data = JSON.parse(message);
      } else if (message.body) {
        data = JSON.parse(message.body);
      }
      
      if (!data) return;
      
      // Store the full update for useChatListUpdates to react to
      setLastCounterUpdate(data);
      
      // Handle unread count updates for conversations
      if (data.conversationId && typeof data.unreadCount === 'number') {
        setMessageUnreadCounts(prev => {
          const updated = new Map(prev);
          // Remove __total__ key when we get real-time per-conversation updates
          // This ensures we use the accurate per-conversation sum instead
          updated.delete('__total__');
          
          // Always store the count (including 0) so ChatList knows there's a real-time update
          updated.set(data.conversationId, data.unreadCount);
          return updated;
        });
      }
    } catch (error) {
      console.error('[NotificationContext] Error parsing counter update:', error);
    }
  }, []);

  // Subscribe to notifications AND message counters (ONLY ONCE)
  useEffect(() => {
    if (!isConnected || !user) {
      return;
    }

    // Subscribe to personal notifications
    const personalSub = subscribe(
      QUEUES.NOTIFICATIONS,
      handleNotification
    );
    
    // Subscribe to message unread counters (for header badge)
    const counterSub = subscribe(
      QUEUES.COUNTERS,
      handleCounterUpdate
    );

    // Cleanup subscriptions
    return () => {
      personalSub.unsubscribe();
      counterSub.unsubscribe();
    };
  }, [isConnected, user, subscribe, handleNotification, handleCounterUpdate]);

  // Fetch initial total unread message count when user connects
  useEffect(() => {
    if (!user || initialMessageCountFetched) return;
    
    const fetchInitialCount = async () => {
      try {
        const totalUnread = await messageService.getTotalUnreadCount();
        if (totalUnread > 0) {
          // Set the total as a single "all" entry since we don't have per-conversation breakdown
          // Real-time updates will track per-conversation
          setMessageUnreadCounts(new Map([['__total__', totalUnread]]));
        }
        setInitialMessageCountFetched(true);
      } catch (error) {
        console.error('[NotificationContext] Error fetching initial unread count:', error);
        setInitialMessageCountFetched(true); // Don't retry on error
      }
    };
    
    fetchInitialCount();
  }, [user, initialMessageCountFetched]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, isRead: true, read: true }
          : notification
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, isRead: true, read: true }))
    );
    setUnreadCount(0);
  }, []);

  // Set unread count (from API)
  const setUnreadCountFromAPI = useCallback((count) => {
    setUnreadCount(count);
  }, []);
  
  // Set message unread counts from API (when loading conversations)
  const setMessageUnreadCountsFromAPI = useCallback((countsMap) => {
    if (countsMap instanceof Map) {
      setMessageUnreadCounts(countsMap);
    } else if (typeof countsMap === 'object') {
      // Convert object to Map
      const map = new Map();
      Object.entries(countsMap).forEach(([conversationId, count]) => {
        if (count > 0) {
          map.set(conversationId, count);
        }
      });
      setMessageUnreadCounts(map);
    }
  }, []);
  
  // Update single conversation unread count (e.g., when marking as read)
  const updateConversationUnreadCount = useCallback((conversationId, count) => {
    setMessageUnreadCounts(prev => {
      const updated = new Map(prev);
      if (count === 0) {
        updated.delete(conversationId);
      } else {
        updated.set(conversationId, count);
      }
      return updated;
    });
  }, []);

  const value = {
    // Notification state
    notifications,
    unreadCount,
    lastUpdate,
    markAsRead,
    markAllAsRead,
    setUnreadCount: setUnreadCountFromAPI,
    
    // Message unread state
    totalUnreadMessages,
    messageUnreadCounts,
    setMessageUnreadCounts: setMessageUnreadCountsFromAPI,
    updateConversationUnreadCount,
    getConversationUnreadCount: (conversationId) => messageUnreadCounts.get(conversationId) || 0,
    lastCounterUpdate, // For useChatListUpdates to react to conversation updates
    
    // Mute handling - set by Messages.js to exclude from nav badge total
    setMutedConversationIds,
    
    isConnected
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
